import React, { useEffect, useMemo, useState } from 'react';
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { VerbConjugationFlowConfig, VerbFlowStep } from '../../../content/types';
import { Input } from '../../../components/ui/Input';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function VerbConjugationFlowRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data?.verbConjugationFlow || question.data || {}) as VerbConjugationFlowConfig;
  const steps: VerbFlowStep[] = config.steps || [];
  const context = config.context || '';
  const [userSteps, setUserSteps] = useState<Record<number, string>>({});
  useEffect(() => {
    setUserSteps({});
  }, [question.id]);
  useEffect(() => {
    const allFilled = steps.every((_, index) => userSteps[index] && userSteps[index].trim() !== '');
    onReadyChange?.(allFilled);
  }, [userSteps, steps.length, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(userSteps), [userSteps]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleInputChange = (index: number, value: string) => {
    if (shouldValidate) return;
    setUserSteps(previous => ({
      ...previous,
      [index]: value
    }));
  };
  const handleChoiceSelect = (index: number, value: string) => {
    if (shouldValidate) return;
    setUserSteps(previous => ({
      ...previous,
      [index]: value
    }));
  };
  const focusChoice = (stepIndex: number, optionIndex: number) => {
    document.getElementById(`verb-flow-${question.id}-${stepIndex}-option-${optionIndex}`)?.focus();
  };
  const handleChoiceKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, stepIndex: number, optionIndex: number, optionCount: number) => {
    if (optionCount === 0) return;
    const isNext = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const isPrevious = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (!isNext && !isPrevious) return;
    event.preventDefault();
    const nextIndex = isNext ? (optionIndex + 1) % optionCount : (optionIndex - 1 + optionCount) % optionCount;
    const nextOption = steps[stepIndex].options?.[nextIndex];
    if (nextOption) {
      handleChoiceSelect(stepIndex, nextOption.value);
      focusChoice(stepIndex, nextIndex);
    }
  };
  const isStepCorrect = (index: number) => {
    const step = steps[index];
    const userValue = userSteps[index]?.trim() || '';
    if (step.type === 'choice') {
      return step.options?.find(option => option.isCorrect)?.value === userValue;
    }
    return (step.acceptedAnswers || []).some(answer => String(answer).trim().toLowerCase() === userValue.toLowerCase());
  };
  return <div className="w-full max-w-xl mx-auto flex flex-col gap-4" data-id="element-12670" data-mp="1psjjllw7moas">
      {context && <div className="px-3 py-2 bg-indigo-50 text-indigo-800 rounded-lg border border-indigo-100 text-sm font-medium" data-id="element-12671" data-mp="1uluqfn10v52p">
          {context}
        </div>}

      <p className="text-center text-sm text-slate-500" data-id="element-12672" data-mp="3ac53t6jrn7zw">
        Werk stap voor stap. Kies bij keuzevragen met de pijltjestoetsen en
        bevestig met Spatie of Enter.
      </p>

      <div className="flex flex-col gap-3" data-id="element-12673" data-mp="3p34845dkjado">
        {steps.map((step, index) => {
        const promptId = `verb-flow-${question.id}-${index}-prompt`;
        let borderClass = 'border-slate-200 bg-white';
        let validationIcon: React.ReactNode = null;
        if (shouldValidate) {
          const correct = isStepCorrect(index);
          borderClass = correct ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50';
          validationIcon = correct ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-12674" data-mp="31afgocwx2v7x" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" data-id="element-12675" data-mp="lk2mqf3iy6f6" />;
        }
        return <motion.div key={index} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className={`p-3 rounded-xl border ${borderClass}`} data-id="element-12676" data-mp="lm5mvrye5fxm" data-mpi={index}>
              
              <div className="flex items-center justify-between gap-2 mb-2" data-id="element-12677" data-mp="2r8v79462erai">
                <div className="flex items-center gap-2" data-id="element-12678" data-mp="2v9f2g272vku0">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold text-xs shrink-0" data-id="element-12679" data-mp="3091ggduqd53v">
                    {index + 1}
                  </span>
                  <span id={promptId} className="font-medium text-slate-700 text-sm" data-id="element-12680" data-mp="3a77iqwkg8d99">
                    
                    {step.prompt}
                  </span>
                </div>
                {validationIcon}
              </div>

              <div className="pl-8" data-id="element-12681" data-mp="3iyhk3a1ppdoi">
                {step.type === 'input' ? <Input id={`verb-flow-${question.id}-${index}-input`} value={userSteps[index] || ''} onChange={event => handleInputChange(index, event.target.value)} disabled={shouldValidate} placeholder="Typ je antwoord..." aria-labelledby={promptId} className="min-h-[44px] text-sm" autoComplete="off" autoCorrect="off" spellCheck={false} data-id="element-12682" data-mp="3nzdwyd41ka62" /> : <div role="radiogroup" aria-labelledby={promptId} className="flex gap-2 flex-wrap" data-id="element-12683" data-mp="1wn20g5haa57">
                  
                    {step.options?.map((option, optionIndex) => {
                const isSelected = userSteps[index] === option.value;
                let buttonClass = 'border border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50';
                if (isSelected && !shouldValidate) {
                  buttonClass = 'border border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200';
                } else if (shouldValidate) {
                  if (option.isCorrect) {
                    buttonClass = 'border border-emerald-500 bg-emerald-50 text-emerald-700';
                  } else if (isSelected) {
                    buttonClass = 'border border-red-500 bg-red-50 text-red-700';
                  } else {
                    buttonClass = 'border border-slate-100 bg-slate-50 text-slate-400 opacity-50';
                  }
                }
                return <button key={option.value} id={`verb-flow-${question.id}-${index}-option-${optionIndex}`} type="button" role="radio" aria-checked={isSelected} tabIndex={isSelected || !userSteps[index] && optionIndex === 0 ? 0 : -1} onClick={() => handleChoiceSelect(index, option.value)} onKeyDown={event => handleChoiceKeyDown(event, index, optionIndex, step.options?.length || 0)} disabled={shouldValidate} className={`min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300 focus-visible:ring-offset-2 ${buttonClass}`} data-id="element-12684" data-mp="32sxgf6evj1nt">
                        
                          {option.label}
                        </button>;
              })}
                  </div>}

                {shouldValidate && step.type === 'choice' && !isStepCorrect(index) && <p className="mt-2 text-xs text-red-600" data-id="element-12685" data-mp="8baqrnr2qpwx">
                      {step.options?.find(option => option.value === userSteps[index])?.feedback || 'Dat is niet juist.'}
                    </p>}
              </div>
            </motion.div>;
      })}
      </div>
    </div>;
}